import { z } from '@repo/schema'
import { GetAddressBookSchema, GetAddressSchema } from '@repo/schema/address'
import { GetChatMessageSchema, GetChatSchema } from '@repo/schema/chat'
import { GetCommentSchema } from '@repo/schema/comment'
import {
  GetCompanyAccountSchema,
  GetCompanyAddressSchema,
  GetCompanyCountrySchema,
  GetCompanySchema,
  GetCompanyStatisticsSchema,
  GetCompanyType,
  GetCompanyXeroAttributesSchema,
} from '@repo/schema/company'
import { GetContactSchema } from '@repo/schema/contact'
import {
  AssignCustomerInputSchema,
  CustomerChangeInputSchema,
  GetCustomerChangeOptionsSchema,
  GetCustomerChangePreviewSchema,
  GetEndCustomerPreviewSchema,
} from '@repo/schema/customer-change'
import { getDataWrapperSchema } from '@repo/schema/data-wrapper'
import {
  GetDocumentSchema,
  GetDocumentTypeEnum,
  GetLoadDropDocumentInternalNoteSchema,
  GetLoadDropDocumentSchema,
  GetMediaSchema,
  GetUnassignedDocumentSchema,
} from '@repo/schema/document'
import { GetDropNoteSchema, GetDropSchema, GetDropStatusSchema } from '@repo/schema/drop'
import { GetCustomAttributesSchema } from '@repo/schema/custom-attributes'
import { GetXeroInvoiceSchema } from '@repo/schema/invoice'
import {
  GetLoadDuplicateStopSchema,
  GetLoadDuplicateTemplateSchema,
} from '@repo/schema/load-duplicate'
import { GetLoadFlagSchema, GetLoadFlagTypeSchema } from '@repo/schema/load-flag'
import { GetHistoryEntrySchema, GetHistoryFiltersSchema } from '@repo/schema/history'
import { GetCompanyLocationSchema } from '@repo/schema/location'
import { GetOrderConfirmationSchema } from '@repo/schema/order-confirmation'
import { GetOrganisationSchema } from '@repo/schema/organisation'
import {
  GetLoadEndCarrierCompanySchema,
  GetLoadEndCarrierSchema,
  GetLoadEndCustomerCompanySchema,
  GetLoadEndCustomerSchema,
  GetLoadSchema,
  GetLoadStatusSchema,
  GetLoadsTotalsSchema,
  GetLoadTypeSchema,
  GetPackagingTypeSchema,
  GetRequirementsSchema,
  LoadMutationResponseSchema,
  UpdateLoadStatusInputSchema,
  WeightUnitSchema,
} from '@repo/schema/load'
import { GetPaginationSchema } from '@repo/schema/pagination'
import { GetPaymentTagTypeSchema } from '@repo/schema/payment-tag'
import { GetCompanyPaymentTermsSchema } from '@repo/schema/payment-terms'
import { GetPlaceSchema } from '@repo/schema/place'
import {
  addPhoneWithCountryValidation,
  createPhoneNumberFieldSchema,
  OptionalPhoneCountryCodeSchema,
} from '@repo/schema/phone'
import {
  LoadFinancialSchema,
  LoadFinancialsSchema,
  LoadFinancialTypeAliasSchema,
} from '@repo/schema/financial'
import { GetRoleSchema } from '@repo/schema/role'
import { GetSourceSchema } from '@repo/schema/source'
import { GetStatusReasonSchema, GetStatusSchema } from '@repo/schema/status'
import { GetLoadTagSchema, GetLoadTagTypeSchema } from '@repo/schema/tag'
import { GetTransportRegistrationPlatesSchema, GetTransportSchema } from '@repo/schema/transport'
import { GetUserSchema } from '@repo/schema/user'
import { reduceTags } from '@repo/sdk/helpers'
import { toBackendDateTimeString } from '@repo/sdk/helpers'
import { UpFetch } from '@repo/upfetch'

import { nextPage } from '../../lib/next-page'
import { historyListInputSchema, historyListParams } from '../../lib/history'
import { createProcedure } from '../../lib/procedure'
import { fetchLoadsList, LoadsListParamsSchema } from './list'
import {
  GetCustomerRiskProfileDefaultCurrencySchema,
  GetCustomerRiskProfileSchema,
} from '@repo/schema/risk-profile'

const MAX_INT32_VALUE = 2147483647

const toBackendDate = (date: Date) =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))

// The API validates any *provided* pallet count as a positive integer.
// Whether pallets are required or must be omitted is enforced by the caller
// (admin form / backend) because the API boundary has no stable packaging-type
// metadata or ID to identify the "Other" type here.
const PalletCountSchema = z.preprocess(
  value => (value === null || value === undefined ? undefined : value),
  z.coerce
    .number({ message: 'Please enter the number of pallets' })
    .int({ message: 'Please enter the number of pallets' })
    .min(1, { message: 'Please enter the number of pallets' })
    .max(MAX_INT32_VALUE, { message: 'Too many pallets' })
    .nullish(),
)

const DraftDropInputSchema = GetDropSchema.partial()
  .omit({ distance_to_next: true, estimated_time_to_next: true })
  .extend({
    location_id: z.number().nullish(),
    place: GetPlaceSchema.partial().optional(),
    type: z.enum(['collection', 'delivery']),
  })

const PublishedDropInputSchema = GetDropSchema.partial({
  id: true,
})
  // Operational timestamps are per-execution state. A load being created has not
  // been executed yet, and the backend rejects them unless formatted `Y-m-d H:i`.
  .omit({
    departure_time: true,
    distance_to_next: true,
    estimated_date: true,
    estimated_time: true,
    estimated_time_to_next: true,
    loading_time: true,
    reported_time: true,
    unloading_time: true,
  })
  .extend({
    location_id: z.number(),
    place: GetPlaceSchema,
  })

const RouteSyncStopInputSchema = GetDropSchema.pick({
  date: true,
  id: true,
  order: true,
  time: true,
  type: true,
}).extend({
  date: z
    .date()
    .nullable()
    .transform(date => (date ? toBackendDate(date) : null)),
  id: z.number().optional(),
  loading_time: z.null().optional(),
  location_id: z.number(),
  order: z.number().int().nonnegative(),
  unloading_time: z.null().optional(),
})

const RouteSyncStopsInputSchema = RouteSyncStopInputSchema.array().superRefine((drops, ctx) => {
  if (drops[0]?.date === null) {
    ctx.addIssue({
      code: 'custom',
      message: 'The first stop must have a date',
      path: [0, 'date'],
    })
  }
})

// The `POST /loads/:loadId/duplicate` body: the template without `source_load_id`, each Stop date
// a calendar day sent as UTC midnight, as a new Load's.
export const CreateDuplicateInputSchema = GetLoadDuplicateTemplateSchema.omit({
  source_load_id: true,
}).extend({
  drops: GetLoadDuplicateStopSchema.extend({
    date: z
      .date()
      .nullable()
      .transform(date => (date ? toBackendDate(date) : null)),
  }).array(),
})

export type CreateDuplicateInput = z.input<typeof CreateDuplicateInputSchema>

const CustomerRateInputSchema = z
  .object({
    amount: z.number().int().nonnegative(),
    currency: z.string(),
    description: z.string().nullish(),
  })
  .optional()

const DraftLoadInputSchema = z.object({
  company_id: z.number({ message: 'Please select a company' }),
  contact_id: z.number().nullable().optional(),
  customer_rate: CustomerRateInputSchema,
  distance: z.number().optional(),
  drops: DraftDropInputSchema.array().optional(),
  end_customer_company_id: z.number().nullish(),
  end_customer_contact_id: z.number().nullish(),
  goods_description: z.string().nullable().optional(),
  load_type_id: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
  organisation_id: z.number().nullable().optional(),
  packaging_type_id: z.number().optional(),
  pallets: z.number().optional(),
  requirements: z
    .object({
      adr: z.boolean(),
      dvs: z.boolean(),
      waste_license: z.boolean(),
    })
    .optional(),
  temperature: z.number().optional(),
  user_id: z.number().nullable().optional(),
  weight: z.number().int().optional(),
  weight_unit: WeightUnitSchema.nullish(),
})

const PublishedLoadBaseInputSchema = z.object({
  company_id: z.number({ message: 'Please select a company' }),
  contact_id: z.number().optional(),
  customer_rate: CustomerRateInputSchema,
  distance: z.number().optional(),
  drops: PublishedDropInputSchema.array(),
  end_customer_company_id: z.number().nullish(),
  end_customer_contact_id: z.number().nullish(),
  goods_description: z.string().nullable(),
  load_type_id: z.number({ message: 'Please select a load type' }),
  notes: z.string().nullable(),
  packaging_type_id: z.preprocess(
    value => (value === null || value === undefined || value === '' ? undefined : value),
    z.coerce.number().optional(),
  ),
  pallets: PalletCountSchema,
  requirements: z.object({
    adr: z.boolean(),
    dvs: z.boolean(),
    waste_license: z.boolean(),
  }),
  status: z
    .enum([
      'draft',
      'to_cover',
      'transport_planning',
      'waiting_for_transit',
      'waiting_for_arrival_confirmation',
      'arrived_at_collection',
      'loading_started',
      'in_transit',
      'arrived_at_destination',
      'unloading_confirmed',
      'waiting_for_docs',
      'completed',
      'archived',
      'cancelled',
    ])
    .default('to_cover'),
  temperature: z.number().optional(),
  user_id: z.number(),
  weight: z.preprocess(
    value => (value === null || value === undefined || value === '' ? undefined : value),
    z
      .number({ message: 'Please enter the weight' })
      .int()
      .min(1, { message: 'Please enter the number of weight' })
      .max(MAX_INT32_VALUE, { message: 'Go on a diet! Too much even for us' })
      .optional(),
  ),
  weight_unit: WeightUnitSchema.nullish(),
})

const PublishedLoadTransform = <T extends { drops: Array<{ date?: Date | null }> }>(data: T) => ({
  ...data,
  drops: data.drops.map((drop, i) => {
    if (!drop.date) return drop

    const localDate = drop.date

    return {
      ...drop,
      date: toBackendDate(localDate),
      order: i,
    }
  }),
})

export const PublishLoadInputSchema = PublishedLoadBaseInputSchema.extend({
  organisation_id: z.number({ message: 'Please select an organisation' }),
}).transform(PublishedLoadTransform)

const CreateLoadInputSchema = PublishedLoadBaseInputSchema.extend({
  organisation_id: z.number().optional(),
}).transform(PublishedLoadTransform)

const UpdateLoadTransportInputSchema = z
  .object({
    cargo_notes: z.string().nullish(),
    driver_email: z.email().nullish(),
    driver_name: z.string().nullish(),
    driver_phone: createPhoneNumberFieldSchema().nullish(),
    driver_phone_country: OptionalPhoneCountryCodeSchema.nullish(),
    posted_truck_id: z.number().nullish(),
    registration_plates: GetTransportRegistrationPlatesSchema.nullish(),
    truck_notes: z.string().nullish(),
  })
  .partial()
  .superRefine((data, ctx) => {
    addPhoneWithCountryValidation(ctx, data.driver_phone, data.driver_phone_country, {
      path: 'driver_phone',
    })
  })

const loadDetailAttributes = [
  'id',
  'documents',
  'status_updating_details',
  'status_history',
  'status_history.reason',
  'status_history.status',
  'status_history.user',
  'status_history.user.avatar',
  'chat',
  'chat.has_unread_messages',
  'assignees',
  'haulier_company',
  'haulier_company.type',
  'haulier_company.account',
  'haulier_company.address',
  'haulier_company.country',
  'haulier_company.xero_attributes',
  'haulier_company.statistics',
  'haulier_contact',
  'haulier_order_reference',
  'haulier_notes',
  'contact',
  'end_customer.company.payment_terms',
  'end_carrier.company.carrier_payment_terms',
  'company.type',
  'company.address',
  'company.country',
  'company.payment_terms',
  'company.default_currency',
  'company.risk_profile',
  'company.risk_profile.verified_by_user',
  'company.xero_attributes',
  'tags',
  'packaging_type',
  'payment_terms',
  'pod_requirement',
  'carrier_payment_terms',
  'organisation',
  'transport',
  'transport.registration_plates',
  'requirements',
  'excel_id',
  'drops.location',
  'drops.location.address',
  'drops.note',
  'drops.distance_to_next',
  'latest_order_confirmation',
  'custom_attributes',
  'last_invoice',
  'financials',
  'flags',
  'flags.type',
  'flags.user',
  'base_currency',
  'estimated_profit_original',
  'estimated_profit_currency',
].join('|')

export const LoadFlagWithRelationsSchema = GetLoadFlagSchema.extend({
  type: GetLoadFlagTypeSchema,
  user: GetUserSchema.nullish(),
})

const commentIncludes = ['user', 'user.avatar'].join('|')

const CommentPageSchema = GetPaginationSchema(
  GetCommentSchema.extend({
    user: GetUserSchema,
  }),
)

const LoadStatusHistoryDetailSchema = z.object({
  id: z.number().nullish(),
  notes: z.string().nullish(),
  reason: z.object({ content: z.string() }).nullish(),
  status: GetStatusSchema,
  updated_at: z.string(),
  user: GetUserSchema.nullish(),
})

const loadDetailParams = {
  'filter[scrapped]': false,
  'filter[without_owner_roles]': 'data-entry',
  i: loadDetailAttributes,
}

const CompanyDetailSchema = GetCompanySchema.extend({
  address: GetAddressSchema.nullish(),
  country: GetCompanyCountrySchema.nullish(),
  payment_terms: GetCompanyPaymentTermsSchema.nullish(),
  risk_profile: GetCustomerRiskProfileSchema.nullish(),
})

const EndCompanyDetailFields = {
  address: GetCompanyAddressSchema.nullish(),
  country: GetCompanyCountrySchema.nullish(),
  statistics: GetCompanyStatisticsSchema.nullish(),
  type: GetCompanyType.nullish(),
  vat: z.string().nullish(),
  xero_attributes: GetCompanyXeroAttributesSchema.nullish(),
}

const ChatDetailSchema = GetChatSchema.extend({
  members: GetUserSchema.array(),
}).nullable()

const DocumentDetailSchema = GetDocumentSchema.extend({
  media: GetMediaSchema,
  user: GetUserSchema.extend({
    roles: GetRoleSchema.array(),
  }),
})

const DropDetailSchema = GetDropSchema.extend({
  location: GetCompanyLocationSchema.nullish(),
  note: GetDropNoteSchema.nullish(),
  place: GetPlaceSchema.extend({
    address_book: GetAddressBookSchema.nullish(),
  }),
  status: GetDropStatusSchema.nullish(),
})

const LoadDetailSchema = GetLoadSchema.extend({
  assignees: z.array(GetUserSchema),
  base_currency: z.string().default('GBP'),
  chat: ChatDetailSchema,
  company: CompanyDetailSchema.extend({
    default_currency: GetCustomerRiskProfileDefaultCurrencySchema.nullish(),
    statistics: GetCompanyStatisticsSchema.nullish(),
    type: GetCompanyType.nullish(),
    xero_attributes: GetCompanyXeroAttributesSchema.nullish(),
  }),
  contact: GetContactSchema.nullish(),
  custom_attributes: z.unknown().array().or(GetCustomAttributesSchema),
  destination: GetPlaceSchema,
  documents: DocumentDetailSchema.array(),
  drops: DropDetailSchema.array(),
  end_carrier: GetLoadEndCarrierSchema.extend({
    company: GetLoadEndCarrierCompanySchema.extend(EndCompanyDetailFields).nullable(),
  }).nullish(),
  end_customer: GetLoadEndCustomerSchema.extend({
    company: GetLoadEndCustomerCompanySchema.extend(EndCompanyDetailFields).nullable(),
  }).nullish(),
  estimated_profit_currency: z.string().nullish(),
  estimated_profit_original: z.number().nullish(),
  excel_id: z.string().nullish(),
  financials: LoadFinancialsSchema.default([]),
  flags: LoadFlagWithRelationsSchema.array().default([]),
  haulier_company: GetCompanySchema.extend({
    account: GetCompanyAccountSchema.nullish(),
    address: GetAddressSchema.nullish(),
    country: GetCompanyCountrySchema.nullish(),
    statistics: GetCompanyStatisticsSchema.nullish(),
    type: GetCompanyType,
    xero_attributes: GetCompanyXeroAttributesSchema.nullish(),
  }).nullish(),
  international: z.boolean(),
  last_invoice: GetXeroInvoiceSchema.nullish(),
  latest_order_confirmation: GetOrderConfirmationSchema.nullish(),
  load_type: GetLoadTypeSchema,
  organisation: GetOrganisationSchema.pick({ id: true, name: true }).nullish(),
  origin: GetPlaceSchema,
  packaging_type: GetPackagingTypeSchema.nullish(),
  requirements: GetRequirementsSchema.nullish(),
  source: GetSourceSchema,
  status: GetStatusSchema,
  status_history: LoadStatusHistoryDetailSchema.array().default([]),
  // Null for loads with no status history row yet (API renders `statusHistory->first()`).
  status_updating_details: z
    .object({
      reason: GetStatusReasonSchema.nullish(),
    })
    .nullish(),
  tags: GetLoadTagSchema.array().default([]).transform(reduceTags),
  transport: GetTransportSchema.extend({
    registration_plates: GetTransportRegistrationPlatesSchema,
  }).nullable(),
  user: GetUserSchema,
})

const DraftDropDetailSchema = GetDropSchema.partial({
  cargo_operations: true,
  date: true,
  departure_time: true,
  distance_to_next: true,
  estimated_date: true,
  estimated_time: true,
  estimated_time_to_next: true,
  id: true,
  name: true,
  order: true,
  reference_no: true,
  reported_time: true,
  time: true,
}).extend({
  location: GetCompanyLocationSchema.nullish(),
  location_id: z.number().nullish(),
  note: GetDropNoteSchema.nullish(),
  place: GetPlaceSchema.partial({ country: true, country_code: true }).nullish(),
  status: GetDropStatusSchema.nullish(),
  type: z.enum(['collection', 'delivery']),
})

const DraftLoadDetailSchema = GetLoadSchema.partial({
  customs: true,
  distance: true,
  height: true,
  international: true,
  ready: true,
  updated_at: true,
  urgent: true,
  width: true,
}).extend({
  assignees: z.array(GetUserSchema).default([]),
  chat: ChatDetailSchema.nullish().transform(value => value ?? null),
  company: CompanyDetailSchema.nullish(),
  company_id: z.number().nullish(),
  contact: GetContactSchema.nullish(),
  contact_id: z.number().nullish(),
  custom_attributes: z
    .record(z.string(), z.unknown())
    .or(z.array(z.unknown()).transform(() => ({})))
    .default({}),
  customs: z.boolean().default(false),
  destination: GetPlaceSchema.nullish(),
  distance: z.number().default(0),
  documents: DocumentDetailSchema.array().default([]),
  drops: DraftDropDetailSchema.array().catch([]),
  excel_id: z.string().nullish(),
  financials: LoadFinancialsSchema.default([]),
  haulier_company: GetCompanySchema.nullish(),
  height: z.number().default(0),
  id: z.number(),
  international: z.boolean().default(false),
  load_type: GetLoadTypeSchema.nullish(),
  organisation_id: z.number().nullish(),
  origin: GetPlaceSchema.nullish(),
  packaging_type: GetPackagingTypeSchema.nullish(),
  ready: z.boolean().default(false),
  requirements: GetRequirementsSchema.partial().nullish(),
  source: GetSourceSchema.nullish(),
  status: GetStatusSchema.refine(val => val.alias === 'draft', {
    message: 'Expected draft status',
  }),
  tags: GetLoadTagSchema.array().default([]).transform(reduceTags),
  updated_at: z.string().default(''),
  urgent: z.boolean().default(false),
  user: GetUserSchema,
  width: z.number().nullish(),
})

// A page of Load chats, newest first, as the Chats lists show them.
const ChatListPageSchema = GetPaginationSchema(
  GetChatSchema.extend({
    count_unread_messages: z.number().nullish(),
    is_joined: z.boolean().default(false),
    last_message: GetChatMessageSchema.nullish(),
    load: GetLoadSchema.extend({
      destination: GetPlaceSchema,
      origin: GetPlaceSchema,
      user: GetUserSchema,
    }),
    members: z.array(GetUserSchema),
  }),
)

// A page of a Load chat's messages, oldest first, each with its sender.
const ChatMessagesPageSchema = GetPaginationSchema(
  GetChatMessageSchema.extend({
    user: GetUserSchema.extend({ roles: GetRoleSchema.array() }),
  }),
)

export type ChatMessagesPage = z.output<typeof ChatMessagesPageSchema>

// The unassigned documents an upload created; callers need only their ids.
const UploadedUnassignedDocumentsSchema = z.object({
  data: GetUnassignedDocumentSchema.pick({ id: true }).array(),
})

const chatListParams = {
  i: ['last_message', 'load', 'load.user', 'is_joined'].join('|'),
  'sort[created_at]': 'desc',
}

// A Load as a search result shows it: its customer, route and status.
const LoadSearchPageSchema = GetPaginationSchema(
  z.object({
    company: z.object({ title: z.string() }).nullish(),
    drops: GetDropSchema.pick({ name: true, type: true }).array().catch([]),
    id: z.number(),
    status: GetLoadStatusSchema.nullish(),
  }),
)

/** GET /loads/reasons: the reasons a user can give for moving a Load to a status. */
export const LoadStatusReasonsSchema = getDataWrapperSchema(
  z.object({ content: z.string(), id: z.number(), order: z.number(), slug: z.string() }).array(),
)

const LoadIdPathSchema = z.object({ loadId: z.number() })
const IdPathSchema = z.object({ id: z.number() })
const ChatIdPathSchema = z.object({ chatId: z.number() })
const BulkLoadIdsInputSchema = z.object({ ids: z.number().array() })
const BulkCancelLoadsInputSchema = BulkLoadIdsInputSchema.extend({
  notes: z.string().optional(),
  reason: z.string(),
})

export const createLoadsRoute = (upFetch: UpFetch) => {
  const p = createProcedure(upFetch)

  return {
    assignee: {
      // POST /loads/:loadId/assignee/:userId/attach
      attach: p
        .path(z.object({ loadId: z.number(), userId: z.number() }), p => [
          'loads',
          p.loadId,
          'assignee',
          p.userId,
          'attach',
        ])
        .mutation(({ path, upFetch }) => upFetch(path, { method: 'POST' })),

      // POST /loads/:loadId/assignee/:userId/detach
      detach: p
        .path(z.object({ loadId: z.number(), userId: z.number() }), p => [
          'loads',
          p.loadId,
          'assignee',
          p.userId,
          'detach',
        ])
        .mutation(({ path, upFetch }) => upFetch(path, { method: 'POST' })),

      // PATCH /loads/:loadId/assignees
      sync: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'assignees'])
        .input(z.object({ user_ids: z.array(z.number()) }))
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PATCH' })),
    },
    bulk: {
      // PATCH /loads/bulk/cancel: cancels every selected load with one reason, or none of them.
      cancel: p
        .path('loads', 'bulk', 'cancel')
        .input(BulkCancelLoadsInputSchema)
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PATCH' })),

      // DELETE /loads/bulk: deletes every selected load.
      delete: p
        .path('loads', 'bulk')
        .input(BulkLoadIdsInputSchema)
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'DELETE' })),
    },
    chats: {
      // POST /loads/users/chat/join: the Current User joins a chat.
      join: p
        .path('loads', 'users', 'chat', 'join')
        .input(z.object({ chat_id: z.number() }))
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'POST' })),

      messages: {
        // POST /loads/chats/:chatId/messages
        create: p
          .path(ChatIdPathSchema, p => ['loads', 'chats', p.chatId, 'messages'])
          .input(z.object({ message: z.string() }))
          .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'POST' })),

        // GET /loads/chats/:chatId/messages (infinite, oldest first)
        list: p
          .path(ChatIdPathSchema, p => ['loads', 'chats', p.chatId, 'messages'])
          .input(z.object({ perPage: z.number() }))
          .infiniteQuery(
            ({ input, page, path, upFetch }) =>
              upFetch(path, {
                params: { i: 'user', page, perPage: input.perPage, 'sort[created_at]': 'asc' },
                schema: ChatMessagesPageSchema,
              }),
            nextPage,
          ),

        // PATCH /loads/chats/:chatId/messages/read: marks the chat's messages read.
        read: p
          .path(ChatIdPathSchema, p => ['loads', 'chats', p.chatId, 'messages', 'read'])
          .mutation(({ path, upFetch }) => upFetch(path, { method: 'PATCH' })),
      },

      // GET /loads/users/chats: the Current User's chats (infinite).
      mine: p
        .path('loads', 'users', 'chats')
        .infiniteQuery(
          ({ page, path, upFetch }) =>
            upFetch(path, { params: { ...chatListParams, page }, schema: ChatListPageSchema }),
          nextPage,
        ),

      // GET /loads/chats: chats without the filtered user (infinite).
      others: p
        .path('loads', 'chats')
        .input(z.object({ 'filter[without_user_id]': z.number().optional() }))
        .infiniteQuery(
          ({ input, page, path, upFetch }) =>
            upFetch(path, {
              params: { ...input, ...chatListParams, page },
              schema: ChatListPageSchema,
            }),
          nextPage,
        ),
    },

    comments: {
      // GET /loads/:loadId/comments (paginated, newest first)
      count: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'comments'])
        .input(z.object({ perPage: z.number() }))
        .query(({ input, path, upFetch }) =>
          upFetch(path, {
            params: {
              i: commentIncludes,
              page: 1,
              perPage: input.perPage,
              'sort[created_at]': 'desc',
            },
            schema: CommentPageSchema,
          }),
        ),

      // POST /loads/comments
      create: p
        .path('loads', 'comments')
        .input(
          z.object({
            content: z.string(),
            load_id: z.number(),
          }),
        )
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'POST' })),

      // DELETE /loads/comments/:id
      delete: p
        .path(IdPathSchema, p => ['loads', 'comments', p.id])
        .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

      // GET /loads/:loadId/comments (infinite)
      list: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'comments'])
        .input(z.object({ perPage: z.number() }))
        .infiniteQuery(
          ({ input, page, path, upFetch }) =>
            upFetch(path, {
              params: {
                i: commentIncludes,
                page,
                perPage: input.perPage,
                'sort[created_at]': 'desc',
              },
              schema: CommentPageSchema,
            }),
          nextPage,
        ),
    },
    create: p
      .path('loads')
      .input(CreateLoadInputSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(LoadMutationResponseSchema),
        }),
      ),

    createDraft: p
      .path('loads', 'draft')
      .input(DraftLoadInputSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(LoadMutationResponseSchema),
        }),
      ),

    // POST /loads/payment-tag-types: creates a custom type, or reactivates an archived one of that name
    createPaymentTagType: p
      .path('loads', 'payment-tag-types')
      .input(z.object({ description: z.string().optional(), name: z.string() }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(GetPaymentTagTypeSchema),
        }),
      ),

    // DELETE /loads/:id
    customer: {
      assign: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'customer'])
        .input(AssignCustomerInputSchema)
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'POST' })),

      options: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'customer', 'options'])
        .query(({ path, upFetch }) =>
          upFetch(path, { schema: getDataWrapperSchema(GetCustomerChangeOptionsSchema) }),
        ),

      preview: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'customer', 'preview'])
        .input(CustomerChangeInputSchema)
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(GetCustomerChangePreviewSchema),
          }),
        ),
    },

    delete: p
      .path(IdPathSchema, p => ['loads', p.id])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

    // DELETE /loads/payment-tag-types/:slug: archives a custom type; documents that have it keep it
    deletePaymentTagType: p
      .path(z.object({ slug: z.string() }), p => ['loads', 'payment-tag-types', p.slug])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

    // DELETE /loads/tag-types/:slug: archives a custom tag type
    deleteTagType: p
      .path(z.object({ slug: z.string() }), p => ['loads', 'tag-types', p.slug])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

    documents: {
      // POST /loads/:loadId/documents/assign
      assign: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'documents', 'assign'])
        .input(z.object({ document_ids: z.number().array() }))
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'POST' })),

      // DELETE /loads/:loadId/documents/:documentId
      delete: p
        .path(z.object({ documentId: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'documents',
          p.documentId,
        ])
        .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

      // GET /loads/:loadId/documents/:documentId/download
      downloadUrl: p
        .path(z.object({ documentId: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'documents',
          p.documentId,
          'download',
        ])
        .query(({ path, upFetch }) => upFetch<{ url: string }>(path)),

      unassigned: {
        // PATCH /loads/documents/unassigned/:documentId
        update: p
          .path(z.object({ documentId: z.number() }), p => [
            'loads',
            'documents',
            'unassigned',
            p.documentId,
          ])
          .input(z.object({ type: GetDocumentTypeEnum.optional() }))
          .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PATCH' })),

        // POST /loads/documents/unassigned: uploads a file not yet assigned to a Load.
        upload: p
          .path('loads', 'documents', 'unassigned')
          .input(z.object({ file: z.instanceof(File), type: GetDocumentTypeEnum.optional() }))
          .mutation(({ input, path, upFetch }) => {
            const formData = new FormData()
            formData.append('type', input.type ?? 'other')
            formData.append('files[]', input.file)
            return upFetch(path, {
              body: formData,
              method: 'POST',
              schema: UploadedUnassignedDocumentsSchema,
            })
          }),
      },
    },

    drop: {
      // PATCH /loads/:loadId/drop/:dropId
      clearLocation: p
        .path(z.object({ dropId: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'drop',
          p.dropId,
        ])
        .input(
          z
            .object({
              location_id: z.null(),
              type: z.enum(['collection', 'delivery']),
            })
            .strict(),
        )
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PATCH' })),

      documents: {
        // DELETE /loads/:loadId/drop/:dropId/documents/:documentId
        delete: p
          .path(z.object({ documentId: z.number(), dropId: z.number(), loadId: z.number() }), p => [
            'loads',
            p.loadId,
            'drop',
            p.dropId,
            'documents',
            p.documentId,
          ])
          .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

        // GET /loads/:loadId/drop/:dropId/documents/:documentId/download
        downloadUrl: p
          .path(z.object({ documentId: z.number(), dropId: z.number(), loadId: z.number() }), p => [
            'loads',
            p.loadId,
            'drop',
            p.dropId,
            'documents',
            p.documentId,
            'download',
          ])
          .query(({ path, upFetch }) => upFetch<{ url: string }>(path)),

        // GET /loads/:loadId/drop/:dropId/documents/internal-note
        internalNote: {
          get: p
            .path(z.object({ dropId: z.number(), loadId: z.number() }), p => [
              'loads',
              p.loadId,
              'drop',
              p.dropId,
              'documents',
              'internal-note',
            ])
            .query(({ path, upFetch }) =>
              upFetch(path, {
                schema: getDataWrapperSchema(GetLoadDropDocumentInternalNoteSchema),
              }),
            ),

          // POST /loads/:loadId/drop/:dropId/documents/internal-note
          set: p
            .path(z.object({ dropId: z.number(), loadId: z.number() }), p => [
              'loads',
              p.loadId,
              'drop',
              p.dropId,
              'documents',
              'internal-note',
            ])
            .input(z.object({ note: z.string().max(300).nullish() }))
            .mutation(({ input, path, upFetch }) =>
              upFetch(path, {
                body: input,
                method: 'POST',
                schema: getDataWrapperSchema(GetLoadDropDocumentInternalNoteSchema),
              }),
            ),
        },

        // GET /loads/:loadId/drop/:dropId/documents
        list: p
          .path(z.object({ dropId: z.number(), loadId: z.number() }), p => [
            'loads',
            p.loadId,
            'drop',
            p.dropId,
            'documents',
          ])
          .input(
            z
              .object({
                'filter[is_internal]': z.boolean().optional(),
                page: z.number().optional(),
                perPage: z.number().optional(),
                search: z.string().optional(),
                'sort[media.name]': z.enum(['asc', 'desc']).optional(),
                'sort[name]': z.enum(['asc', 'desc']).optional(),
              })
              .optional(),
          )
          .query(({ input, path, upFetch }) =>
            upFetch(path, {
              params: input ?? {},
              schema: GetPaginationSchema(GetLoadDropDocumentSchema),
            }),
          ),

        // GET /loads/:loadId/drop/:dropId/documents/:documentId/preview
        previewUrl: p
          .path(z.object({ documentId: z.number(), dropId: z.number(), loadId: z.number() }), p => [
            'loads',
            p.loadId,
            'drop',
            p.dropId,
            'documents',
            p.documentId,
            'preview',
          ])
          .query(({ path, upFetch }) => upFetch<{ url: string }>(path)),

        // POST /loads/:loadId/drop/:dropId/documents
        upload: p
          .path(z.object({ dropId: z.number(), loadId: z.number() }), p => [
            'loads',
            p.loadId,
            'drop',
            p.dropId,
            'documents',
          ])
          .input(
            z.object({
              files: z.array(z.instanceof(File)),
              is_internal: z.boolean(),
            }),
          )
          .mutation(({ input, path, upFetch }) => {
            const formData = new FormData()
            formData.append('is_internal', String(input.is_internal))
            for (const file of input.files) {
              formData.append('files[]', file)
            }
            return upFetch(path, {
              body: formData,
              method: 'POST',
              schema: getDataWrapperSchema(z.array(GetLoadDropDocumentSchema)),
            })
          }),
      },

      notes: {
        // POST /loads/:loadId/drops/:dropId/notes
        create: p
          .path(z.object({ dropId: z.number(), loadId: z.number() }), p => [
            'loads',
            p.loadId,
            'drop',
            p.dropId,
            'notes',
          ])
          .input(z.object({ comment: z.string() }))
          .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'POST' })),

        // PATCH /loads/:loadId/drops/:dropId/notes/:noteId
        update: p
          .path(z.object({ dropId: z.number(), loadId: z.number(), noteId: z.number() }), p => [
            'loads',
            p.loadId,
            'drop',
            p.dropId,
            'notes',
            p.noteId,
          ])
          .input(z.object({ comment: z.string() }))
          .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PATCH' })),
      },

      // PATCH /loads/:loadId/drop/:dropId
      update: p
        .path(z.object({ dropId: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'drop',
          p.dropId,
        ])
        .input(
          GetDropSchema.partial()
            .omit({
              departure_time: true,
              estimated_date: true,
              loading_time: true,
              reported_time: true,
              unloading_time: true,
            })
            .extend({
              departure_time: z.date().transform(toBackendDateTimeString).nullish(),
              // A calendar day at UTC midnight, sent as `Y-m-d`.
              estimated_date: z
                .date()
                .transform(date => date.toISOString().slice(0, 10))
                .nullish(),
              loading_time: z.date().transform(toBackendDateTimeString).nullish(),
              place: GetPlaceSchema.partial().optional(),
              reported_time: z.date().transform(toBackendDateTimeString).nullish(),
              unloading_time: z.date().transform(toBackendDateTimeString).nullish(),
            }),
        )
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PATCH' })),

      // PATCH /loads/:loadId/drops/:dropId/address
      updateAddress: p
        .path(z.object({ dropId: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'drop',
          p.dropId,
          'address',
        ])
        .input(z.object({ address_id: z.number() }))
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PATCH' })),

      // PATCH /loads/:loadId/drops/:dropId/address
      updateLocation: p
        .path(z.object({ dropId: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'drop',
          p.dropId,
          'address',
        ])
        .input(z.object({ location_id: z.number() }))
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PATCH' })),

      // PATCH /loads/:loadId/drops/:dropId
      updateName: p
        .path(z.object({ dropId: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'drop',
          p.dropId,
        ])
        .input(z.object({ name: z.string().nullable(), type: z.enum(['collection', 'delivery']) }))
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PATCH' })),
    },

    drops: {
      replace: p
        .path(LoadIdPathSchema, path => ['loads', path.loadId, 'drops'])
        .input(z.object({ drops: RouteSyncStopsInputSchema }))
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'POST' })),
    },

    duplicate: {
      // POST /loads/:loadId/duplicate: creates a Duplicate of the Original Load `loadId`. Not
      // idempotent: every request creates a Load.
      create: p
        .path(LoadIdPathSchema, path => ['loads', path.loadId, 'duplicate'])
        .input(CreateDuplicateInputSchema)
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(LoadMutationResponseSchema),
          }),
        ),

      // GET /loads/:loadId/duplicate: the Original Load's details a Duplicate starts from.
      template: p
        .path(LoadIdPathSchema, path => ['loads', path.loadId, 'duplicate'])
        .query(({ path, upFetch }) =>
          upFetch(path, { schema: getDataWrapperSchema(GetLoadDuplicateTemplateSchema) }),
        ),
    },

    endCarrier: {
      // POST /loads/:loadId/end-carrier
      assign: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'end-carrier'])
        .input(
          z.object({
            end_carrier_company_id: z.number(),
            end_carrier_contact_id: z.number().nullish(),
          }),
        )
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(LoadMutationResponseSchema),
          }),
        ),

      // DELETE /loads/:loadId/end-carrier
      remove: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'end-carrier'])
        .mutation(({ path, upFetch }) =>
          upFetch(path, {
            method: 'DELETE',
            schema: getDataWrapperSchema(LoadMutationResponseSchema),
          }),
        ),
    },

    endCustomer: {
      // POST /loads/:loadId/end-customer
      assign: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'end-customer'])
        .input(
          z.object({
            end_customer_company_id: z.number(),
            end_customer_contact_id: z.number().nullish(),
          }),
        )
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(LoadMutationResponseSchema),
          }),
        ),

      preview: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'end-customer', 'preview'])
        .input(z.object({ end_customer_company_id: z.number() }))
        .query(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(GetEndCustomerPreviewSchema),
          }),
        ),

      // DELETE /loads/:loadId/end-customer
      remove: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'end-customer'])
        .mutation(({ path, upFetch }) =>
          upFetch(path, {
            method: 'DELETE',
            schema: getDataWrapperSchema(LoadMutationResponseSchema),
          }),
        ),
    },

    financials: {
      // POST /loads/:loadId/financials/:type
      create: p
        .path(z.object({ loadId: z.number(), type: LoadFinancialTypeAliasSchema }), p => [
          'loads',
          p.loadId,
          'financials',
          p.type,
        ])
        .input(
          z.object({
            amount: z.number(),
            currency: z.string(),
            description: z.string().nullish(),
            title: z.string().nullish(),
          }),
        )
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(LoadFinancialSchema),
          }),
        ),

      // DELETE /loads/:loadId/financials/:financialId
      delete: p
        .path(z.object({ financialId: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'financials',
          p.financialId,
        ])
        .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

      // GET /loads/:loadId/financials
      list: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'financials'])
        .query(({ path, upFetch }) =>
          upFetch(path, { schema: getDataWrapperSchema(LoadFinancialsSchema) }),
        ),

      // PATCH /loads/:loadId/financials/:financialId
      update: p
        .path(z.object({ financialId: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'financials',
          p.financialId,
        ])
        .input(
          z.object({
            amount: z.number().optional(),
            currency: z.string().optional(),
            description: z.string().nullish(),
            title: z.string().nullish(),
          }),
        )
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'PATCH',
            schema: getDataWrapperSchema(LoadFinancialSchema),
          }),
        ),
    },

    flags: {
      // POST /loads/:loadId/flags
      create: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'flags'])
        .input(
          z.object({
            flag_type_id: z.number(),
            notes: z.string().max(160).nullish(),
          }),
        )
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(LoadFlagWithRelationsSchema),
          }),
        ),

      // DELETE /loads/:loadId/flags/:flagId
      delete: p
        .path(z.object({ flagId: z.number(), loadId: z.number() }), p => [
          'loads',
          p.loadId,
          'flags',
          p.flagId,
        ])
        .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),
    },

    flagTypes: {
      // GET /loads/flag-types
      list: p
        .path('loads', 'flag-types')
        .query(({ path, upFetch }) =>
          upFetch(path, { schema: getDataWrapperSchema(GetLoadFlagTypeSchema.array()) }),
        ),
    },

    get: {
      draft: p
        .path(IdPathSchema, p => ['loads', p.id])
        .input(z.object({ status: z.literal('draft') }))
        .query(({ path, upFetch }) =>
          upFetch(path, {
            params: loadDetailParams,
            schema: getDataWrapperSchema(DraftLoadDetailSchema),
          }),
        ),

      full: p
        .path(IdPathSchema, p => ['loads', p.id])
        .query(({ path, upFetch }) =>
          upFetch(path, {
            params: loadDetailParams,
            schema: getDataWrapperSchema(LoadDetailSchema),
          }),
        ),
    },

    getSources: p.path('loads', 'sources').query(({ path, upFetch }) =>
      upFetch(path, {
        schema: getDataWrapperSchema(GetSourceSchema.array()),
      }),
    ),

    //GET /loads/statuses
    getStatuses: p.path('loads', 'statuses').query(({ path, upFetch }) => {
      return upFetch(path, {
        schema: getDataWrapperSchema(GetLoadStatusSchema.array()),
      })
    }),

    haulier: {
      // POST /loads/:loadId/haulier
      assign: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'haulier'])
        .input(
          z.object({ haulier_company_id: z.number(), haulier_contact_id: z.number().nullish() }),
        )
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'POST' })),

      // DELETE /loads/:loadId/haulier
      remove: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'haulier'])
        .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),
    },

    history: {
      // GET /loads/:loadId/history/filters
      filters: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'history', 'filters'])
        .query(({ path, upFetch }) =>
          upFetch(path, {
            schema: getDataWrapperSchema(GetHistoryFiltersSchema),
          }),
        ),

      // GET /loads/:loadId/history
      list: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'history'])
        .input(historyListInputSchema)
        .infiniteQuery(
          ({ input, page, path, upFetch }) =>
            upFetch(path, {
              params: historyListParams(input, page),
              schema: GetPaginationSchema(GetHistoryEntrySchema),
            }),
          nextPage,
        ),
    },

    // GET /loads page by page from page 1, for the Loads list's infinite scroll.
    infiniteList: p
      .path('loads')
      .input(LoadsListParamsSchema)
      .infiniteQuery(
        ({ input, page, path, upFetch }) =>
          fetchLoadsList({ params: { ...input, page }, path, upFetch }),
        nextPage,
      ),

    issues: {
      create: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'issues'])
        .input(
          z.object({
            note: z.string().max(5000).nullish(),
            reason: z.string(),
          }),
        )
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(LoadFlagWithRelationsSchema),
          }),
        ),
    },

    // GET /loads: one page of the Loads list. `list.allScopesKey({ input: {} })` matches every
    // Loads list query in every Organisation scope, infiniteList included.
    list: p
      .path('loads')
      .input(LoadsListParamsSchema)
      .query(({ input, path, upFetch }) => fetchLoadsList({ params: input, path, upFetch })),

    orderConfirmations: {
      // DELETE /loads/:loadId/order-confirmations/:orderConfirmationId
      delete: p
        .path(z.object({ loadId: z.number(), orderConfirmationId: z.number() }), p => [
          'loads',
          p.loadId,
          'order-confirmations',
          p.orderConfirmationId,
        ])
        .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

      // GET /loads/:loadId/order-confirmations/:orderConfirmationId/download
      downloadUrl: p
        .path(z.object({ loadId: z.number(), orderConfirmationId: z.number() }), p => [
          'loads',
          p.loadId,
          'order-confirmations',
          p.orderConfirmationId,
          'download',
        ])
        .query(({ path, upFetch }) => upFetch<{ url: string }>(path)),

      // POST /loads/:loadId/order-confirmations/generate
      generate: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'order-confirmations', 'generate'])
        .mutation(({ path, upFetch }) =>
          upFetch(path, {
            method: 'POST',
            schema: getDataWrapperSchema(GetOrderConfirmationSchema),
          }),
        ),

      // GET /loads/:loadId/order-confirmations
      list: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'order-confirmations'])
        .query(({ path, upFetch }) =>
          upFetch(path, {
            schema: getDataWrapperSchema(GetOrderConfirmationSchema.array()),
          }),
        ),

      // GET /loads/:loadId/order-confirmations/:orderConfirmationId/preview
      previewUrl: p
        .path(z.object({ loadId: z.number(), orderConfirmationId: z.number() }), p => [
          'loads',
          p.loadId,
          'order-confirmations',
          p.orderConfirmationId,
          'preview',
        ])
        .query(({ path, upFetch }) => upFetch<{ url: string }>(path)),

      // POST /loads/:loadId/order-confirmations
      upload: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'order-confirmations'])
        .input(z.object({ file: z.instanceof(File) }))
        .mutation(({ input, path, upFetch }) => {
          const formData = new FormData()
          formData.append('file', input.file)
          return upFetch(path, {
            body: formData,
            method: 'POST',
            schema: getDataWrapperSchema(GetOrderConfirmationSchema),
          })
        }),
    },

    // PATCH /loads/:id (generic)
    patch: p
      .path(IdPathSchema, p => ['loads', p.id])
      .input(
        z.record(z.string(), z.unknown()).superRefine((data, ctx) => {
          if ('company_id' in data) {
            ctx.addIssue({
              code: 'custom',
              message:
                'company_id cannot be patched: change the customer through loads.customer.assign so the options, preview and acknowledgement flow is applied.',
              path: ['company_id'],
            })
          }
        }),
      )
      .mutation(({ input, path, upFetch }) =>
        upFetch<{ data: { id: number } }>(path, { body: input, method: 'PATCH' }),
      ),

    // GET /loads/payment-tag-types: active types only, sorted by order
    paymentTagTypes: p
      .path('loads', 'payment-tag-types')
      .query(({ path, upFetch }) =>
        upFetch(path, { schema: getDataWrapperSchema(GetPaymentTagTypeSchema.array()) }),
      ),

    publish: p
      .path(IdPathSchema, p => ['loads', p.id, 'publish'])
      .input(PublishLoadInputSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(LoadMutationResponseSchema),
        }),
      ),

    //GET /loads/reasons
    reasons: p
      .path('loads', 'reasons')
      .input(z.object({ status: z.string() }))
      .query(({ input, path, upFetch }) =>
        upFetch(path, {
          params: input,
          schema: LoadStatusReasonsSchema,
        }),
      ),

    // GET /loads matching a search, as search results (cmd-K). Keyed apart from the Loads lists
    // that request the same path, so their keys and filter prefixes never reach its results.
    search: p
      .path('loads')
      .key('loads', 'search')
      .input(z.object({ perPage: z.number(), search: z.string() }))
      .query(({ input, path, upFetch }) =>
        upFetch(path, {
          params: { ...input, i: 'company|drops|status' },
          schema: LoadSearchPageSchema,
        }),
      ),

    tags: {
      // POST /loads/:loadId/tags
      add: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'tags'])
        .input(z.object({ tag_slug: z.string() }))
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'POST' })),

      // POST /loads/:loadId/tags/custom
      createCustom: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'tags', 'custom'])
        .input(z.object({ description: z.string().optional(), name: z.string() }))
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'POST' })),

      // DELETE /loads/:loadId/tags
      remove: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'tags'])
        .input(z.object({ tag_slug: z.string() }))
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'DELETE' })),
    },

    // GET /loads/tag-types
    tagTypes: p.path('loads', 'tag-types').query(({ path, upFetch }) =>
      upFetch(path, {
        schema: getDataWrapperSchema(
          GetLoadTagTypeSchema.array().transform(tags =>
            tags?.reduce<{
              admin: typeof tags
              all: typeof tags
              system: typeof tags
            }>(
              (acc, tag) => {
                if (tag.is_system) {
                  acc.system.push(tag)
                } else {
                  acc.admin.push(tag)
                }
                acc.all.push(tag)
                return acc
              },
              { admin: [], all: [], system: [] },
            ),
          ),
        ),
      }),
    ),

    // GET /loads/totals: totals for every Load matching the Loads list params. The API
    // ignores page, perPage, and sort, and each request scans every matching Load.
    totals: p
      .path('loads', 'totals')
      .input(z.record(z.string(), z.union([z.number(), z.string()]).optional()))
      .query(({ input, path, upFetch }) =>
        upFetch(path, { params: input, schema: getDataWrapperSchema(GetLoadsTotalsSchema) }),
      ),

    transport: {
      // PATCH /loads/:loadId/transport
      update: p
        .path(LoadIdPathSchema, p => ['loads', p.loadId, 'transport'])
        .input(UpdateLoadTransportInputSchema)
        .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PATCH' })),
    },

    // PATCH /loads/:id with input
    update: p
      .path(IdPathSchema, p => ['loads', p.id])
      .input(
        z.object({
          company_id: z.number().optional(),
          contact_id: z.number().optional(),
          end_customer_company_id: z.number().nullish(),
          end_customer_contact_id: z.number().nullish(),
          user_id: z.number().optional(),
        }),
      )
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'PATCH',
          schema: getDataWrapperSchema(LoadMutationResponseSchema),
        }),
      ),
    updateDraft: p
      .path(IdPathSchema, p => ['loads', p.id])
      .input(DraftLoadInputSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'PATCH',
          schema: getDataWrapperSchema(LoadMutationResponseSchema),
        }),
      ),

    // PATCH /loads/:id/status
    updateStatus: p
      .path(IdPathSchema, p => ['loads', p.id, 'status'])
      .input(UpdateLoadStatusInputSchema)
      .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PATCH' })),
  }
}
